import type { ComboboxFieldOption, ComboboxFieldQueryResult, ComboboxFieldValue } from './types'

export const DEFAULT_COMBOBOX_FIELD_QUERY_DEBOUNCE_MS = 250
export const DEFAULT_COMBOBOX_FIELD_LOAD_MORE_OFFSET = 24
export const DEFAULT_VIRTUALIZED_COMBOBOX_ITEM_HEIGHT = 32
export const DEFAULT_VIRTUALIZED_COMBOBOX_GROUP_LABEL_HEIGHT = 30
export const DEFAULT_VIRTUALIZED_COMBOBOX_SEPARATOR_HEIGHT = 9
export const DEFAULT_VIRTUALIZED_COMBOBOX_OVERSCAN = 10
const DEFAULT_COMBOBOX_FIELD_PAGE_SIZE = 20

export const normalizeFilterSelectorValues = (options: {
    value: ComboboxFieldValue
    multiple: boolean
    normalizeCaseInsensitive: boolean
}): string[] => {
    const { value, multiple, normalizeCaseInsensitive } = options
    const sourceValues = Array.isArray(value) ? value : typeof value === 'string' && !!value ? [value] : []

    const dedupeMap = new Map<string, string>()

    sourceValues.forEach((currentValue) => {
        if (typeof currentValue !== 'string' || !currentValue) {
            return
        }

        const key = normalizeCaseInsensitive ? currentValue.toLowerCase() : currentValue
        if (!dedupeMap.has(key)) {
            dedupeMap.set(key, currentValue)
        }
    })

    const normalizedValues = Array.from(dedupeMap.values())

    if (multiple) {
        return normalizedValues
    }

    return normalizedValues.length > 0 ? [normalizedValues[0]] : []
}

export const areFilterSelectorValuesEqual = (options: {
    left: string[]
    right: string[]
    normalizeCaseInsensitive: boolean
}): boolean => {
    const { left, right, normalizeCaseInsensitive } = options

    if (left.length !== right.length) {
        return false
    }

    return left.every((leftValue, index) => {
        const rightValue = right[index]
        if (normalizeCaseInsensitive) {
            return rightValue?.toLowerCase() === leftValue.toLowerCase()
        }

        return rightValue === leftValue
    })
}

export const toFilterSelectorValue = (values: string[], multiple: boolean): ComboboxFieldValue => {
    if (multiple) {
        return values
    }

    return values[0] || null
}

export const isCloseByEscape = (eventDetails: unknown): boolean => {
    if (!eventDetails || typeof eventDetails !== 'object' || !('event' in eventDetails)) {
        return false
    }

    const event = eventDetails.event
    return event instanceof KeyboardEvent && event.key === 'Escape' && event.type === 'keydown' && event.isTrusted
}

export const normalizeComboboxFieldValue = (value: ComboboxFieldValue, multiple: boolean): string[] | string | null => {
    if (multiple) {
        if (Array.isArray(value)) {
            return value.filter((item): item is string => typeof item === 'string' && !!item)
        }

        if (typeof value === 'string' && !!value) {
            return [value]
        }

        return []
    }

    if (Array.isArray(value)) {
        const [firstValue] = value
        return typeof firstValue === 'string' && !!firstValue ? firstValue : null
    }

    if (typeof value === 'string' && !!value) {
        return value
    }

    return null
}

export const getComboboxFieldValueIds = (value: string[] | string | null, multiple: boolean): string[] => {
    if (multiple) {
        return Array.isArray(value) ? value : []
    }

    return typeof value === 'string' && !!value ? [value] : []
}

export const areComboboxFieldOptionsEqual = (left: ComboboxFieldOption[], right: ComboboxFieldOption[]): boolean => {
    if (left.length !== right.length) {
        return false
    }

    return left.every((leftOption, index) => {
        const rightOption = right[index]
        return !!rightOption && leftOption.value === rightOption.value && leftOption.label === rightOption.label
    })
}

export const mergeUniqueComboboxFieldOptions = (
    ...sources: Array<ComboboxFieldOption[] | undefined>
): ComboboxFieldOption[] => {
    const optionIndexMap = new Map<string, number>()
    const options: ComboboxFieldOption[] = []

    sources.forEach((source) => {
        source?.forEach((option) => {
            const existingOptionIndex = optionIndexMap.get(option.value)
            if (existingOptionIndex === undefined) {
                optionIndexMap.set(option.value, options.length)
                options.push(option)
                return
            }

            options[existingOptionIndex] = option
        })
    })

    return options
}

export const getNormalizedComparableValue = (value: string, normalizeCaseInsensitive: boolean): string =>
    normalizeCaseInsensitive ? value.toLowerCase() : value

export const resolveFallbackSelectedOptions = (options: {
    values: string[]
    availableOptions?: ComboboxFieldOption[]
    normalizeCaseInsensitive: boolean
}): ComboboxFieldOption[] => {
    const { values, availableOptions, normalizeCaseInsensitive } = options
    const sourceOptions = availableOptions || []

    const findMatchingOption = (valueToMatch: string): ComboboxFieldOption | null => {
        if (normalizeCaseInsensitive) {
            const lowerValueToMatch = valueToMatch.toLowerCase()
            return (
                sourceOptions.find((option) => option.value.toLowerCase() === lowerValueToMatch) || {
                    value: valueToMatch,
                    label: valueToMatch,
                }
            )
        }

        return (
            sourceOptions.find((option) => option.value === valueToMatch) || {
                value: valueToMatch,
                label: valueToMatch,
            }
        )
    }

    return values
        .map((draftValue) => findMatchingOption(draftValue))
        .filter((option): option is ComboboxFieldOption => !!option)
}

export const isAbortError = (error: unknown): boolean =>
    !!error && typeof error === 'object' && 'name' in error && error.name === 'AbortError'

export const resolveHasMore = (options: {
    result: ComboboxFieldQueryResult
    loadedCount: number
    currentCount: number
    pageSize: number
    knownTotalCount?: number | null
}): boolean => {
    const { result, loadedCount, currentCount, pageSize, knownTotalCount } = options

    if (typeof result.hasMore === 'boolean') {
        return result.hasMore
    }

    if (typeof result.totalCount === 'number') {
        return currentCount + loadedCount < result.totalCount
    }

    if (typeof knownTotalCount === 'number') {
        return currentCount + loadedCount < knownTotalCount
    }

    return loadedCount >= pageSize
}

export const hasVisibleGroupLabel = (label: string) => label.replace(/\u200B/g, '').trim().length > 0

export { DEFAULT_COMBOBOX_FIELD_PAGE_SIZE }
